import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { sendChatMessage } from "../../../core/data/redux/aiChatSlice";


// ─── Constants ───────────────────────────────────────────────────────────────
const WELCOME_TEXT =
    "Welcome to the DMRC Employee Assistant! 👋\n\nI'm here to help you with all your queries. How can I assist you today?";
const DEFAULT_RESPONSE =
    "I'm sorry, I couldn't process that. Please try again or contact HR at ext. 2345.";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Message {
    id: number;
    text: string;
    isBot: boolean;
    timestamp: Date;
    isTyping?: boolean;
}

interface ChatbotModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// ─── Cross-browser voice loader ───────────────────────────────────────────────
// Chrome/Edge fire voiceschanged async; Firefox/Safari may have them immediately.
const loadVoices = (): Promise<SpeechSynthesisVoice[]> =>
    new Promise((resolve) => {
        const immediate = window.speechSynthesis.getVoices();
        if (immediate.length > 0) return resolve(immediate);

        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            resolve(window.speechSynthesis.getVoices());
        };

        window.speechSynthesis.onvoiceschanged = finish;
        // Hard timeout – some browsers never fire the event (Firefox quirk)
        setTimeout(finish, 1500);
    });

// ─── Female Indian voice picker (priority chain) ──────────────────────────────
const FEMALE_NAMES = [
    "aditi", "neerja", "swara", "heera", "veena",
    "pallavi", "raveena", "lekha", "kajal", "female",
];
const FEMALE_EN = ["samantha", "zira", "karen", "victoria", "moira", "fiona", "susan"];

const pickVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null => {
    const isLocal = (v: SpeechSynthesisVoice) => !v.name.toLowerCase().includes("online");
    const isFemale = (v: SpeechSynthesisVoice) => {
        const n = v.name.toLowerCase();
        return FEMALE_NAMES.some((k: string) => n.includes(k)) || FEMALE_EN.some((k: string) => n.includes(k)) || n.includes("female");
    };

    // 1. Safe Local Indian Female (e.g., Microsoft Heera)
    let v = voices.find(v => isLocal(v) && (v.lang.includes("-IN") || v.lang.startsWith("hi")) && isFemale(v));
    if (v) return v;

    // 2. Safe Local English Female (e.g., Microsoft Zira, Apple Samantha)
    v = voices.find(v => isLocal(v) && v.lang.startsWith("en") && isFemale(v));
    if (v) return v;

    // 3. Online Indian Female (Microsoft Neerja Online - might crash in strict mode Edge)
    v = voices.find(v => !isLocal(v) && (v.lang.includes("-IN") || v.lang.startsWith("hi")) && isFemale(v));
    if (v) return v;

    // 4. Any Safe Local English Voice (Fallback)
    v = voices.find(v => isLocal(v) && v.lang.startsWith("en"));
    if (v) return v;

    return voices[0] || null;
};

// ─── Text cleaner for TTS ─────────────────────────────────────────────────────
const cleanForTTS = (text: string) =>
    text
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/<[^>]+>/g, "")
        .replace(/[✓✗#*_~`]/g, "")
        // Strip emoji (surrogate pairs / high codepoints)
        .replace(/[\u{1F000}-\u{1FFFF}]/gu, "")
        .replace(/\s+/g, " ")
        .trim();

// ─── Response extractor ───────────────────────────────────────────────────────
const extractText = (raw: any): string => {
    const d = raw?.data ?? raw ?? {};
    let parsed = d?.response;
    if (typeof parsed === "string") {
        try { parsed = JSON.parse(parsed); } catch { /* keep as string */ }
    }
    if (parsed?.detail && Array.isArray(parsed.detail))
        return `Sorry, there was an error: ${parsed.detail[0]?.msg ?? "Validation error"}`;

    return (
        parsed?.answer ?? parsed?.message ?? parsed?.text ?? parsed?.response ??
        d?.answer ??
        (d?.message && d.message !== "Message sent" ? d.message : null) ??
        (typeof parsed === "string" && parsed ? parsed : null) ??
        DEFAULT_RESPONSE
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
const ChatbotModal: React.FC<ChatbotModalProps> = ({ isOpen, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();

    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [speakingId, setSpeakingId] = useState<number | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const sessionIdRef = useRef<string | null>(null);
    const isListeningRef = useRef(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

    // Preload voices on mount so they're ready when user clicks speak
    useEffect(() => {
        if ("speechSynthesis" in window) loadVoices();
    }, []);

    // Welcome message
    useEffect(() => {
        if (isOpen && messages.length === 0)
            setMessages([{ id: 1, text: WELCOME_TEXT, isBot: true, timestamp: new Date() }]);
    }, [isOpen]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Focus input
    useEffect(() => {
        if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
    }, [isOpen]);

    // ── speak (chunked to avoid synthesis-failed on long texts) ───────────────
    const speak = useCallback(
        async (text: string, id: number) => {
            if (!("speechSynthesis" in window)) {
                toast.error("Text-to-speech is not supported in this browser.");
                return;
            }

            // Toggle off
            if (speakingId === id) {
                window.speechSynthesis.cancel();
                setSpeakingId(null);
                utteranceRef.current = null;
                return;
            }

            window.speechSynthesis.cancel();
            setSpeakingId(id);

            const clean = cleanForTTS(text);
            if (!clean) { setSpeakingId(null); return; }

            // Split into sentence chunks ≤180 chars to prevent synthesis-failed
            // Chrome/Edge fail on long single utterances
            const chunks = clean
                .split(/(?<=[.!?।])/)
                .reduce<string[]>((acc, s) => {
                    const t = s.trim();
                    if (!t) return acc;
                    const last = acc[acc.length - 1];
                    if (last && (last + " " + t).length <= 180)
                        acc[acc.length - 1] = last + " " + t;
                    else
                        acc.push(t);
                    return acc;
                }, []);

            if (!chunks.length) { setSpeakingId(null); return; }

            try {
                const voices = await loadVoices();
                const voice  = pickVoice(voices);

                const speakChunk = (index: number, useFallbackVoice = false) => {
                    if (index >= chunks.length) { setSpeakingId(null); utteranceRef.current = null; return; }

                    const utt = new SpeechSynthesisUtterance(chunks[index]);
                    utteranceRef.current = utt;

                    // Global GC protection (fixes Edge dropping the utterance object silently)
                    const w = window as any;
                    w.__tts_gc = w.__tts_gc || [];
                    w.__tts_gc.push(utt);

                    const cleanup = () => {
                        w.__tts_gc = (w.__tts_gc || []).filter((u: any) => u !== utt);
                    };

                    // If fallback is triggered, do NOT set voice or lang at all — let OS decide safely
                    if (!useFallbackVoice && voice) {
                        utt.voice = voice;
                        utt.lang = voice.lang;
                    }

                    utt.onend = () => {
                        cleanup();
                        speakChunk(index + 1);
                    };
                    
                    utt.onerror = (e) => {
                        cleanup();
                        if (e.error === "interrupted" || e.error === "canceled") return;
                        console.warn("TTS chunk error:", e.error);
                        
                        // Edge synthesis-failed crash handler:
                        if (!useFallbackVoice && voice) {
                            console.warn("Retrying chunk with default system voice...");
                            setTimeout(() => speakChunk(index, true), 100);
                        } else {
                            speakChunk(index + 1);
                        }
                    };

                    // Edge requires a solid delay after cancel() before speaking a new queue.
                    // Also removed resume() before speak(), as it can corrupt Edge's TTS queue.
                    setTimeout(() => {
                        window.speechSynthesis.speak(utt);
                        // iOS Safari sometimes needs resume *after* speak to wake up
                        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
                    }, index === 0 ? 400 : 50);
                };

                speakChunk(0);
            } catch {
                setSpeakingId(null);
            }
        },
        [speakingId]
    );

    // ── beep ────────────────────────────────────────────────────────────────────
    const playBeep = (type: "on" | "off") => {
        try {
            const AC = window.AudioContext ?? (window as any).webkitAudioContext;
            if (!AC) return;
            const ctx = new AC();
            const tone = (freq: number, t: number, dur: number) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g); g.connect(ctx.destination);
                o.type = "sine"; o.frequency.setValueAtTime(freq, t);
                g.gain.setValueAtTime(0, t);
                g.gain.linearRampToValueAtTime(0.5, t + 0.02);
                g.gain.exponentialRampToValueAtTime(0.01, t + dur);
                o.start(t); o.stop(t + dur);
            };
            if (type === "on") { tone(600, ctx.currentTime, 0.15); tone(800, ctx.currentTime + 0.15, 0.2); }
            else { tone(600, ctx.currentTime, 0.15); tone(450, ctx.currentTime + 0.15, 0.2); }
        } catch { /* silent */ }
    };

    // ── mic toggle ──────────────────────────────────────────────────────────────
    const toggleListening = async () => {
        if (isListeningRef.current) {
            playBeep("off");
            mediaRecorderRef.current?.stop();
            setIsListening(false);
            isListeningRef.current = false;
            return;
        }

        window.speechSynthesis.cancel();
        setSpeakingId(null);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            recorder.onstart = () => { playBeep("on"); setIsListening(true); isListeningRef.current = true; };
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                if (!audioChunksRef.current.length) return;

                const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
                setIsTyping(true);
                try {
                    const fd = new FormData();
                    fd.append("model", "saaras:v3");
                    fd.append("mode", "transcribe");
                    fd.append("file", blob, "speech.wav");

                    const sarvamKey = process.env.REACT_APP_SARVAM_API_KEY || "";
                    const headers: Record<string, string> = {};
                    if (sarvamKey) {
                        headers["api-subscription-key"] = sarvamKey;
                    }

                    const res = await fetch("https://api.sarvam.ai/speech-to-text", {
                        method: "POST",
                        headers,
                        body: fd,
                    });
                    if (!res.ok) {
                        throw new Error("Speech service currently unavailable");
                    }
                    const data = await res.json();
                    if (data?.transcript?.trim()) handleSend(data.transcript, true);
                    else toast.error("Could not transcribe audio. Please try again.");
                } catch {
                    toast.error("Speech transcription service is currently unavailable.");
                } finally {
                    setIsTyping(false);
                }
            };
            recorder.start();
        } catch {
            toast.error("Microphone access is required for speech recognition.");
        }
    };

    // ── Read language from employee settings (falls back to "en") ───────────────
    const settingsData = useSelector((state: RootState) => state.settingsState?.data);
    const userLanguage: string =
        settingsData?.data?.settings?.language?.value ||
        settingsData?.settings?.language?.value ||
        "en";

    // ── Dark mode: watch html[data-theme] attribute reactively ─────────────────
    const [isDark, setIsDark] = useState(() =>
        document.documentElement.getAttribute("data-theme") === "dark" ||
        localStorage.getItem("dataTheme") === "dark"
    );
    useEffect(() => {
        const check = () =>
            setIsDark(
                document.documentElement.getAttribute("data-theme") === "dark" ||
                localStorage.getItem("dataTheme") === "dark"
            );
        const mo = new MutationObserver(check);
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
        return () => mo.disconnect();
    }, []);

    // ── send ────────────────────────────────────────────────────────────────────
    const sendChat = useCallback(
        async (text: string) => {
            const payload: any = { message: text, language: userLanguage };
            if (sessionIdRef.current) payload.session_id = sessionIdRef.current;
            return dispatch(sendChatMessage(payload)).unwrap();
        },
        [dispatch, userLanguage]
    );

    const handleSend = async (text?: string, isVoice = false) => {
        const msg = (text ?? inputText).trim();
        if (!msg) return;

        setMessages((p) => [...p, { id: Date.now(), text: msg, isBot: false, timestamp: new Date() }]);
        setInputText("");
        inputRef.current?.blur();

        // typing indicator
        setIsTyping(true);
        setMessages((p) => [...p, { id: -1, text: "", isBot: true, timestamp: new Date(), isTyping: true }]);

        try {
            const result = await sendChat(msg);
            setIsTyping(false);
            setMessages((p) => p.filter((m) => !m.isTyping));

            // Persist session
            const sid = result?.session_id ?? result?.data?.session_id;
            if (sid) sessionIdRef.current = sid;

            const responseText = extractText(result);
            const botId = Date.now() + 1;
            setMessages((p) => [...p, { id: botId, text: responseText, isBot: true, timestamp: new Date() }]);

            if (isVoice) speak(responseText, botId);
        } catch (err: any) {
            setIsTyping(false);
            setMessages((p) => p.filter((m) => !m.isTyping));
            // Friendly message for CORS / network / timeout errors
            const isNetwork = !err?.response || err?.message?.includes("Network") || err?.message?.includes("CORS");
            const errText = isNetwork
                ? "⚠️ Could not reach the assistant server. Please check your connection or try again later."
                : "Sorry, I encountered an error. Please try again later.";
            setMessages((p) => [
                ...p,
                { id: Date.now() + 1, text: errText, isBot: true, timestamp: new Date() },
            ]);
        }
    };

    const handleClose = () => {
        window.speechSynthesis.cancel();
        sessionIdRef.current = null;
        setMessages([{ id: 1, text: WELCOME_TEXT, isBot: true, timestamp: new Date() }]);
        onClose();
    };

    const fmt = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

    if (!isOpen) return null;

    // ── Theme tokens ─────────────────────────────────────────────────────────────
    const T = {
        panel:      isDark ? "#1a1a2e"   : "#f5f5f5",
        msgArea:    isDark ? "#1a1a2e"   : "#f5f5f5",
        inputBar:   isDark ? "#16213e"   : "#f5f5f5",
        inputBg:    isDark ? "#0f3460"   : "white",
        inputBorder:isDark ? "#2a4a7f"   : "transparent",
        inputColor: isDark ? "#e0e0e0"   : "#333",
        botBubble:  isDark ? "#1e3a5f"   : "white",
        botColor:   isDark ? "#e0e0e0"   : "#333",
        divider:    isDark ? "#2a3a5c"   : "#e0e0e0",
        sendBg:     isDark ? "#0f3460"   : "white",
        sendBorder: isDark ? "#2a4a7f"   : "#e0e0e0",
        speakActive:isDark ? "#c0282c"   : "white",
        speakActClr:isDark ? "white"     : "#c0282c",
        scrollThumb:isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
    };

    const S = {
        bubble: (isBot: boolean): React.CSSProperties => ({
            maxWidth: "75%", padding: "12px 16px",
            borderRadius: isBot ? "16px 16px 16px 4px" : "16px 16px 4px 16px",
            background: isBot ? T.botBubble : "linear-gradient(135deg,#c0282c,#e03236)",
            color: isBot ? T.botColor : "white",
            boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.05)",
            wordBreak: "break-word" as const,
            animation: "msgPop 0.3s cubic-bezier(0.175,0.885,0.32,1.275) forwards",
        }),
        dot: (delay: string): React.CSSProperties => ({
            width: 8, height: 8, borderRadius: "50%",
            backgroundColor: isDark ? "#5a7a9f" : "#999",
            animation: `bounce 1.4s infinite ${delay}`,
        }),
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={handleClose}
                style={{
                    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)",
                    backdropFilter: "blur(4px)", zIndex: 9998, animation: "fadeIn 0.3s ease"
                }}
            />

            {/* Panel */}
            <div style={{
                position: "fixed", top: 0, right: 0, bottom: 0, width: 420, maxWidth: "100vw",
                backgroundColor: T.panel, zIndex: 9999, display: "flex", flexDirection: "column",
                boxShadow: "-4px 0 16px rgba(0,0,0,0.2)", animation: "slideIn 0.3s ease",
                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif"
            }}>

                {/* Header */}
                <div style={{
                    background: "linear-gradient(135deg,#c0282c,#e03236)", color: "white",
                    padding: "16px 20px", display: "flex", alignItems: "center",
                    justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: "50%", backgroundColor: "white",
                            display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"
                        }}>
                            <img src="/assets/img/login/ladies.png" alt="Assistant"
                                style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.15)" }} />
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "white" }}>DMRC Assistant</h4>
                            <small style={{ opacity: 0.9, fontSize: 12 }}>Online</small>
                        </div>
                    </div>
                    <button onClick={handleClose} aria-label="Close"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        style={{
                            background: "none", border: "none", color: "white", fontSize: 24,
                            cursor: "pointer", padding: "4px 8px", borderRadius: 4, transition: "background 0.2s"
                        }}>
                        ×
                    </button>
                </div>

                {/* Messages */}
                <div className="cb-scroll" style={{
                    flex: 1, overflowY: "auto", padding: 16,
                    display: "flex", flexDirection: "column", gap: 16,
                    backgroundColor: T.msgArea
                }}>
                    <div style={{ flex: 1 }} />

                    {messages.map((m) => (
                        <div key={m.id} style={{
                            display: "flex", alignItems: "flex-start", gap: 8,
                            justifyContent: m.isBot ? "flex-start" : "flex-end"
                        }}>

                            {m.isBot && (
                                <div style={{
                                    width: 32, height: 32, borderRadius: "50%", backgroundColor: "white",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    flexShrink: 0, overflow: "hidden"
                                }}>
                                    <img src="/assets/img/login/ladies.png" alt="bot"
                                        style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.15)" }} />
                                </div>
                            )}

                            <div style={S.bubble(m.isBot)}>
                                {m.isTyping ? (
                                    <div style={{ display: "flex", gap: 4 }}>
                                        {["0s", "0.2s", "0.4s"].map((d, i) => <div key={i} style={S.dot(d)} />)}
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(
                                                    m.text
                                                        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                                                        .replace(/\n/g, "<br/>")
                                                ),
                                            }} />
                                        <div style={{
                                            fontSize: 11, opacity: 0.7, marginTop: 4,
                                            textAlign: m.isBot ? "right" : "left"
                                        }}>{fmt(m.timestamp)}</div>
                                    </>
                                )}
                            </div>

                            {m.isBot && !m.isTyping && (
                                <button onClick={() => speak(m.text, m.id)}
                                    title={speakingId === m.id ? "Stop" : "Play message"}
                                    style={{
                                        width: 30, height: 30, borderRadius: "50%", border: "none",
                                        backgroundColor: speakingId === m.id ? T.speakActive : "#c0282c",
                                        color: speakingId === m.id ? T.speakActClr : "white",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        cursor: "pointer", marginLeft: 8, alignSelf: "flex-end",
                                        marginBottom: 4, flexShrink: 0, transition: "all 0.3s",
                                        boxShadow: speakingId === m.id ? "0 2px 8px rgba(192,40,44,0.4)" : "none",
                                        animation: speakingId === m.id ? "pulse 1.5s infinite" : "none"
                                    }}>
                                    {speakingId === m.id ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <rect x="6" y="6" width="12" height="12" rx="2" />
                                        </svg>
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                                        </svg>
                                    )}
                                </button>
                            )}
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input bar */}
                <div style={{
                    padding: "12px 16px", backgroundColor: T.inputBar,
                    borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 8
                }}>

                    <div style={{
                        display: "flex", flex: 1, backgroundColor: T.inputBg, borderRadius: 24,
                        padding: "4px 8px", alignItems: "center",
                        boxShadow: isDark ? "none" : "0 2px 4px rgba(0,0,0,0.05)",
                        border: `1px solid ${T.inputBorder}`
                    }}>
                        <input ref={inputRef} type="text" value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !isTyping) { e.preventDefault(); handleSend(); } }}
                            placeholder="Please type here..."
                            style={{
                                flex: 1, padding: 10, border: "none", fontSize: 14,
                                outline: "none", backgroundColor: "transparent",
                                color: T.inputColor
                            }} />

                        {/* Mic button */}
                        <button onClick={toggleListening} disabled={isTyping}
                            title={isListening ? "Stop listening" : "Start speaking"}
                            style={{
                                width: 36, height: 36, borderRadius: "50%", marginRight: 4, flexShrink: 0,
                                backgroundColor: isListening
                                    ? (isDark ? "#2a4a7f" : "white")
                                    : (isTyping ? (isDark ? "#2a2a3e" : "#e0e0e0") : "#c0282c"),
                                border: isListening ? `1px solid #c0282c` : "none",
                                color: isListening ? "#c0282c" : (isTyping ? "#999" : "white"),
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: isTyping ? "not-allowed" : "pointer", transition: "all 0.3s",
                                opacity: isTyping ? 0.6 : 1,
                                animation: isListening ? "pulse 1.5s infinite" : "none"
                            }}>
                            {isListening ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="6" height="6" rx="1" />
                                    <circle cx="12" cy="12" r="10" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    <line x1="12" y1="19" x2="12" y2="23" />
                                    <line x1="8" y1="23" x2="16" y2="23" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Send button */}
                    <button onClick={() => handleSend()} disabled={!inputText.trim() || isTyping}
                        style={{
                            width: 44, height: 44, borderRadius: "50%", backgroundColor: T.sendBg,
                            border: `1px solid ${T.sendBorder}`, flexShrink: 0, transition: "all 0.2s",
                            color: inputText.trim() && !isTyping ? "#c0282c" : "#666",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: inputText.trim() && !isTyping ? "pointer" : "not-allowed",
                            boxShadow: isDark ? "none" : "0 2px 4px rgba(0,0,0,0.05)"
                        }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Animations */}
            <style>{`
        @keyframes slideIn   { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes fadeIn    { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bounce    { 0%,60%,100% { transform: translateY(0); } 30% { transform: translateY(-10px); } }
        @keyframes msgPop    { from { opacity:0; transform:translateY(10px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes pulse     {
          0%   { box-shadow: 0 0 0 0 rgba(192,40,44,0.4); transform: scale(0.95); }
          70%  { box-shadow: 0 0 0 6px rgba(192,40,44,0); transform: scale(1.05); }
          100% { box-shadow: 0 0 0 0 rgba(192,40,44,0);   transform: scale(0.95); }
        }
        .cb-scroll::-webkit-scrollbar       { width: 6px; }
        .cb-scroll::-webkit-scrollbar-track { background: transparent; }
        .cb-scroll::-webkit-scrollbar-thumb { background: ${T.scrollThumb}; border-radius: 10px; }
        .cb-scroll::-webkit-scrollbar-thumb:hover { background: ${isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"}; }
      `}</style>
        </>
    );
};

export default ChatbotModal;
